import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { SPRITES } from "@buildlab/shared/sprites";
import { profileApi, type Me, type Profile, type ProfileStats } from "./api";
import { Lion } from "./Lion";
import { SignOutButton, canSignOut } from "./SignOut";
import { Avatar, SpriteIcon, initials } from "./Sprite";
import "./Profile.css";

// The name menu on Home (Paul, Oct 10): click your name for "My profile" and "Sign out". The profile
// lets you pick an icon (decision 84: names come from the sign-in account, nobody renames themselves)
// and shows fun stats about what you've built (apps/api/src/profile.ts counts them). Parent emails
// are the teacher's job, on the class roster (decision 85). Words are for a 7th–8th grade reader.

/** The avatar and name in the Home header, as a button that opens a small menu. */
export function NameMenu({ me }: { me: Me }) {
  const name = me.user.name;
  const [avatar, setAvatar] = useState<string | null>(me.user.avatar ?? null);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => wrap.current && !wrap.current.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="name-menu" ref={wrap}>
      <button type="button" className="name-btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Avatar name={name} avatar={avatar} />
        <span className="name-text">{name}</span>
        <span className="name-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="name-menu-panel" role="menu" aria-label="Your account">
          <button
            type="button"
            role="menuitem"
            className="name-menu-item"
            autoFocus
            onClick={() => {
              setOpen(false);
              setProfile(true);
            }}
          >
            My profile
          </button>
          {canSignOut ? (
            <SignOutButton className="name-menu-item" role="menuitem" />
          ) : (
            <p className="muted small name-menu-note">Local test mode: there's nothing to sign out of.</p>
          )}
        </div>
      )}
      {profile && <ProfilePanel onClose={() => setProfile(false)} onAvatar={setAvatar} />}
    </div>
  );
}

const MODE_STYLE: Record<NonNullable<ProfileStats["favoriteMode"]>, { title: string; line: string }> = {
  build: { title: "Builder", line: "You use Build mode the most. You like to make things happen!" },
  plan: { title: "Planner", line: "You use Plan mode the most. You think before you build. Smart!" },
  ask: { title: "Explorer", line: "You use Ask mode the most. You want to know how things work." },
};

const fmt = (n: number) => n.toLocaleString();
const plural = (n: number, one: string, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
const day = (t: number) => new Date(t).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });

/** The profile dialog: your icon, fun stats and sign out. */
export function ProfilePanel({ onClose, onAvatar }: { onClose: () => void; onAvatar: (avatar: string | null) => void }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [stats, setStats] = useState<ProfileStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    profileApi.get().then(
      (r) => {
        setProfile(r.profile);
        setStats(r.stats);
      },
      (e: Error) => setError(e.message),
    );
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, []);

  const saved = (p: Profile) => {
    setProfile(p);
    onAvatar(p.avatar);
  };
  const picked = SPRITES.find((x) => x.id === profile?.avatar);

  return (
    <div className="dlg-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg profile-dlg" role="dialog" aria-modal="true" aria-labelledby="profile-title">
        <div className="profile-head">
          {picked ? (
            <span className="profile-icon">
              <SpriteIcon id={picked.id} size={80} label={`Your icon: ${picked.name}`} />
            </span>
          ) : (
            <Lion pose={stats && stats.projects > 0 ? "cheer" : "wave"} size={92} label="Roary the lion" />
          )}
          <div className="profile-head-text">
            <span className="eyebrow">My profile</span>
            <h2 id="profile-title">{profile?.name ?? "…"}</h2>
            {stats && <LevelBar level={stats.level} />}
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <p className="dlg-error">{error}</p>}
        {!profile && !error && <p className="muted">Loading your profile…</p>}
        {profile && <IconPicker profile={profile} onSaved={saved} />}
        {stats && <StatsGrid stats={stats} />}

        <div className="dlg-foot">
          <SignOutButton className="outline small" />
          <button type="button" className="primary small" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/** "No icon" (initials) plus every sprite, in a fixed grid so arrow keys can move up and down. */
const CHOICES: { id: string | null; name: string }[] = [{ id: null, name: "No icon (show my initials)" }, ...SPRITES.map((x) => ({ id: x.id, name: x.name }))];
export const PICKER_COLUMNS = 6;
const SAVE_DELAY_MS = 450;

/**
 * Pick your icon (decision 84). A radio group: Tab lands on the chosen icon, arrow keys (and Home/End)
 * move the choice, a click picks. A click saves at once; arrow keys save once the choice stops moving.
 */
function IconPicker({ profile, onSaved }: { profile: Profile; onSaved: (p: Profile) => void }) {
  const [choice, setChoice] = useState<string | null>(profile.avatar);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const savedId = useRef<string | null>(profile.avatar);
  const latest = useRef<string | null>(profile.avatar);
  const delay = useRef(0); // a click saves at once; arrow keys wait for the choice to stop moving
  latest.current = choice;

  const save = async (id: string | null) => {
    const r = await profileApi.save({ avatar: id });
    savedId.current = r.profile.avatar;
    onSaved(r.profile);
  };

  useEffect(() => {
    if (choice === savedId.current) return;
    const timer = window.setTimeout(() => {
      setMsg(null);
      save(choice).then(
        () => setMsg({ ok: true, text: "Saved!" }),
        (e: Error) => setMsg({ ok: false, text: e.message }),
      );
    }, delay.current);
    return () => window.clearTimeout(timer);
  }, [choice]);
  // Closed while a choice was still waiting to save: save it anyway (the header updates).
  useEffect(
    () => () => {
      if (latest.current !== savedId.current) void save(latest.current).catch(() => undefined);
    },
    [],
  );

  const index = Math.max(0, CHOICES.findIndex((x) => x.id === choice));
  const move = (to: number) => {
    delay.current = SAVE_DELAY_MS;
    const i = Math.min(CHOICES.length - 1, Math.max(0, to));
    setChoice(CHOICES[i]!.id);
    buttons.current[i]?.focus();
  };
  const onKey = (e: ReactKeyboardEvent) => {
    const step: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: PICKER_COLUMNS, ArrowUp: -PICKER_COLUMNS };
    if (e.key in step) move(index + step[e.key]!);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(CHOICES.length - 1);
    else return;
    e.preventDefault();
  };
  const current = CHOICES[index]!;

  return (
    <section className="profile-section" aria-labelledby="icon-pick-label">
      <span className="profile-label" id="icon-pick-label">
        Your icon
      </span>
      <p className="muted small">Pick an icon to show next to your name. You can change it any time.</p>
      <div className="icon-grid" role="radiogroup" aria-labelledby="icon-pick-label" onKeyDown={onKey}>
        {CHOICES.map((x, i) => {
          const on = x.id === choice;
          return (
            <button
              key={x.id ?? "none"}
              ref={(el) => {
                buttons.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={x.name}
              title={x.name}
              tabIndex={on ? 0 : -1}
              className={`icon-choice${on ? " on" : ""}`}
              onClick={() => {
                delay.current = 0;
                setChoice(x.id);
              }}
            >
              {x.id ? (
                <SpriteIcon id={x.id} size={32} />
              ) : (
                <span className="icon-none" aria-hidden="true">
                  {initials(profile.name) || "?"}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="small icon-current" aria-live="polite">
        <b>{current.id ? current.name : "No icon"}</b>
        {msg && <span className={msg.ok ? "profile-ok" : "dlg-error"}> {msg.text}</span>}
      </p>
    </section>
  );
}

function LevelBar({ level }: { level: ProfileStats["level"] }) {
  const pct = level.next ? Math.round(((level.points - level.from) / (level.next.at - level.from)) * 100) : 100;
  return (
    <div className="level">
      <span className="level-name">{level.name}</span>
      <span className="level-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`Level progress: ${pct}%`}>
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="small level-next">
        {level.next ? `${plural(level.points, "point")}. ${fmt(level.next.at - level.points)} more to ${level.next.name}!` : `${plural(level.points, "point")}. Top level. Roary is proud!`}
      </span>
    </div>
  );
}

function StatsGrid({ stats }: { stats: ProfileStats }) {
  const style = stats.favoriteMode ? MODE_STYLE[stats.favoriteMode] : null;
  const tiles: { big: string; label: string; note?: string }[] = [
    { big: fmt(stats.projects), label: stats.projects === 1 ? "Project built" : "Projects built", note: stats.lessonsStarted ? `In ${plural(stats.lessonsStarted, "lesson")}` : undefined },
    { big: fmt(stats.helperMessages), label: "Messages to your helper" },
    { big: `~${fmt(stats.lines)}`, label: "Lines in your projects", note: "Your code, notes and pages" },
    { big: fmt(stats.savePoints), label: stats.savePoints === 1 ? "Save point" : "Save points" },
    { big: fmt(stats.daysActive), label: stats.daysActive === 1 ? "Day you built" : "Days you built", note: stats.bestStreak > 1 ? `Best streak: ${stats.bestStreak} school days in a row` : undefined },
    { big: fmt(stats.submissions), label: "Turned in" },
    {
      big: fmt(stats.gallery.shared),
      label: "Shared with the class",
      note: stats.gallery.remixedByOthers ? `Classmates remixed your work ${plural(stats.gallery.remixedByOthers, "time")}!` : stats.gallery.remixesMade ? `You remixed ${plural(stats.gallery.remixesMade, "project")}` : undefined,
    },
  ];
  if (stats.coinsSpent !== null) tiles.push({ big: fmt(stats.coinsSpent), label: stats.coinsSpent === 1 ? "Coin spent" : "Coins spent", note: "1 coin = 1 helper message" });
  return (
    <section className="profile-section" aria-label="Your stats">
      <span className="profile-label">Your stats</span>
      {stats.projects === 0 ? (
        <p className="muted">No projects yet. Pick a lesson on Home to start, and your stats will show up here.</p>
      ) : (
        <>
          {style && (
            <p className="profile-style">
              <b>Your style: {style.title}.</b> {style.line}
            </p>
          )}
          <ul className="stat-tiles">
            {tiles.map((t) => (
              <li key={t.label} className="stat-tile">
                <span className="stat-big">{t.big}</span>
                <span className="stat-label">{t.label}</span>
                {t.note && <span className="stat-note">{t.note}</span>}
              </li>
            ))}
          </ul>
          <ul className="profile-facts">
            {stats.topProject && (
              <li>
                Most worked on: <b>{stats.topProject.title}</b> ({plural(stats.topProject.helperMessages, "message")})
              </li>
            )}
            {stats.firstProjectAt && <li>First project: {day(stats.firstProjectAt)}</li>}
          </ul>
        </>
      )}
    </section>
  );
}
